import {useConfig} from '@aboutyou/config';
import {Brand} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/brand.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {SizeSelection_Size} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  COLORS,
  ContentPopper,
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  Image,
  mobileOnlyLinaria,
  SizeBubble,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {useCustomerIsVip} from '@aboutyou/user';
import {styled} from '@linaria/react';
import React, {FC, useEffect, useState} from 'react';
import sc from 'styled-components';
import {SizeReminder} from '../SizeReminder/SizeReminder.lazy';
import {
  SizeSelectionOpenedStateKey,
  useSizeSelectionOpenedState,
} from '../SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';

export interface SizeReminderSizeProps {
  sizeId: number;
  label: string;
}

export const useSizeReminderState = (initialIds: number[] = []) => {
  const [sizeReminderSize, setSizeReminderSize] = useState<
    SizeSelection_Size | undefined
  >();
  const [sizeReminderSizeIds, setSizeReminderSizeIds] = useState(initialIds);

  const toggleSoldOutSizeId = (id: number) => {
    const updatedList = sizeReminderSizeIds.includes(id)
      ? sizeReminderSizeIds.filter(entry => entry !== id)
      : [...sizeReminderSizeIds, id];
    setSizeReminderSizeIds(updatedList);
  };

  const showSizeReminder = (size: SizeSelection_Size | undefined) => {
    setSizeReminderSize(size);
  };

  const hideSizeReminder = () => {
    setSizeReminderSize(undefined);
  };

  const resetSizeReminderSelection = () => setSizeReminderSizeIds(initialIds);

  useEffect(() => {
    if (sizeReminderSizeIds !== initialIds) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- TODO: Cleanup setState in useEffect https://aboutyou.atlassian.net/browse/ASFE-7602
      setSizeReminderSizeIds(initialIds);
    }
  }, [initialIds.join(',')]);

  return {
    toggleSoldOutSizeId,
    showSizeReminder,
    hideSizeReminder,
    resetSizeReminderSelection,
    sizeReminderSizeIds,
    sizeReminderSize,
  };
};

const SizeSelectionComponent = ({
  sizeSelectionOpenedStateKey,
  selectedSizeLabel,
  isOneSize,
}: {
  sizeSelectionOpenedStateKey: SizeSelectionOpenedStateKey;
  selectedSizeLabel: string | undefined;
  isOneSize: boolean;
}) => {
  const {t} = useTranslation();
  const {open: openSizeSelection} = useSizeSelectionOpenedState(
    sizeSelectionOpenedStateKey,
  );

  const {device} = useConfig();

  if (device === 'desktop') {
    return null;
  }

  const onSizeClick = () => {
    if (isOneSize) {
      return;
    }
    openSizeSelection();
  };

  return (
    <SizeSelectionWrapper
      data-testid="sizeSelectionDropdown"
      onClick={onSizeClick}
    >
      {selectedSizeLabel || t('Select your size')}

      <StyledIcon asset={GenericIconName.ArrowDown} />
    </SizeSelectionWrapper>
  );
};

export const SizeSectionSizeReminder: FC<{
  brand?: Brand;
  onResetSizeReminderSize: () => void;
  product?: Product;
  sizeReminderSize: SizeReminderSizeProps | undefined;
  SizeReminderLayoutComponent: ContentPopperProps['LayoutComponent'];
  sizeSelectionOpenedStateKey: SizeSelectionOpenedStateKey | undefined;
  isOneSize?: boolean;
  isOpen: boolean;
}> = ({
  brand,
  onResetSizeReminderSize,
  product,
  sizeReminderSize,
  SizeReminderLayoutComponent,
  sizeSelectionOpenedStateKey,
  isOneSize,
  isOpen,
}) => {
  const isVip = useCustomerIsVip();
  const {t} = useTranslation();
  const brandLogo = brand?.blackLogoImage?.url;
  const brandName = brand?.name;
  const productImage = product?.primaryBustImage?.src;
  const productName = product?.name;
  const productId = product?.id;

  return (
    <ContentPopper
      LayoutComponent={SizeReminderLayoutComponent}
      data-testid="sizeSectionSizeReminder"
      fixedSmallWidth
      renderInPortal
      headerContent={
        <Header>
          <Title>{t('Notify me when this is available')}</Title>
        </Header>
      }
      onClose={onResetSizeReminderSize}
      open={isOpen}
      bodyContent={
        <BodyContent>
          {isVip && (
            <Hint data-testid="sizeReminderVipNote">
              {t(`As a VIP, you'll receive updates first!`)}
            </Hint>
          )}

          <ProductWrapper>
            {productImage && (
              <ProductImageWrapper>
                <ProductImage
                  alt={brandName}
                  applyBorderRadius
                  breakpoints={[]}
                  data-testid="sizeSectionSizeReminderProductImage"
                  ratio="3:4"
                  src={productImage}
                  widths={[200]}
                />
              </ProductImageWrapper>
            )}
            <ProductDetails>
              {brandLogo && (
                <BrandLogo
                  alt={brandName}
                  breakpoints={[]}
                  ratio={`2:1`}
                  src={brandLogo}
                  widths={[200]}
                />
              )}

              {productName && <ProductName>{productName}</ProductName>}
              {sizeReminderSize && (
                <SizeReminderContent>
                  <SizeBubble
                    active
                    size={{
                      label: sizeReminderSize.label,
                      sizeId: sizeReminderSize.sizeId,
                    }}
                  />
                </SizeReminderContent>
              )}

              {sizeSelectionOpenedStateKey && (
                <SizeSelectionComponent
                  sizeSelectionOpenedStateKey={sizeSelectionOpenedStateKey}
                  selectedSizeLabel={sizeReminderSize?.label}
                  isOneSize={!!isOneSize}
                />
              )}
            </ProductDetails>
          </ProductWrapper>
        </BodyContent>
      }
      footerContent={
        <SizeReminder
          onBackButtonClick={onResetSizeReminderSize}
          onSubmissionProcessed={onResetSizeReminderSize}
          productId={productId}
          {...(sizeReminderSize
            ? {selectedSizeIds: [sizeReminderSize.sizeId]}
            : {})}
        />
      }
    />
  );
};

const StyledIcon = styled(Icon)`
  width: 8px;
  fill: ${COLORS.Grey1};
`;

const SizeSelectionWrapper = sc.div`
  border: 1px solid ${COLORS.Grey3};
  border-radius: 3px;
  height: 48px;
  min-width: 160px;
  display: flex;
  align-items: center;
  font-size: 14px;
  justify-content: space-between;
  padding: 0 15px;
  margin-top: 10px;
  gap: 10px;
  white-space: nowrap;
`;

const Header = sc.header<{hasBadges?: boolean}>`
  width: 100%;
  box-sizing: border-box;
  text-align: center;

  ${mobileOnlyLinaria(`
    margin-top: 5px;
  `)}
`;

const Title = sc.span`
  ${mobileOnlyLinaria(`
    font-size: ${FONT_SIZES.px18};
    font-weight: ${FONT_WEIGHTS.bold};
  `)}
`;

const BodyContent = sc.div`
  ${mobileOnlyLinaria(`
    margin: -16px;
  `)}
`;

const Hint = sc.span`
  width: 100%;
  background-color: ${COLORS.Black};
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  text-align: center;
  padding: 0.375rem 0;
  display: flex;
  justify-content: center;

  ${tabletAndUpLinaria(`
    // 100% + 32px to compensate for the -16px of margin on left and right
    width: calc(100% + 32px);
    margin: -16px -16px 20px;
  `)}
`;

const ProductWrapper = sc.div`
  display: flex;

  ${mobileOnlyLinaria(`
    padding: 20px;
    overflow: hidden;
  `)}
`;

const ProductImageWrapper = sc.div`
  position: relative;
  height: 150px;
  width: 120px;
  padding: 1rem 0;
  display: flex;
  justify-content: center;
  background-color: ${COLORS.ProductImageBackground};
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 4px;
`;

const ProductImage = sc(Image)`
  max-height: 100%;
  max-width: 100%;
  object-fit: contain;
`;

const ProductDetails = sc.div`
  flex: 1;
  padding: 0 1rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const BrandLogo = sc(Image)`
  object-fit: contain;
  object-position: left;
  width: 100%;
  max-width: 100px;
  max-height: 50px;
  margin-top: -0.75rem;
`;

const ProductName = sc.span`
  color: ${COLORS.Black};
  font-size: 0.8125rem;
  font-weight: ${FONT_WEIGHTS.mediumBold};
  margin-top: 0.375rem;
`;

const SizeReminderContent = sc.div`
  margin: 1rem 0 0.5rem;
`;
